import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import * as constants from '../../constants/residenceModalConstants';
import Button from '../Button';
import Select from '../Select';
import SelectBase from '../SelectBase';
import './index.scss';

const ResidenceSelection = ({
  countries,
  fetchStates,
  states,
  updateResidence,
  setEditState,
  initialCountry,
  initialState,
  initialStartDate,
  index,
}) => {
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [livedSinceMonth, setLivedSinceMonth] = useState('');
  const [livedSinceYear, setLivedSinceYear] = useState('');
  const [showYearWarning, setShowYearWarning] = useState(false);
  const [showMonthWarning, setShowMonthWarning] = useState(false);

  useEffect(() => {
    if (country && country.hasOwnProperty('value')) {
      if (Object.keys(states).includes(country.value)) {
        setStateOptions(states[country.value]);
      }
    } else {
      setState(null);
      setStateOptions([]);
    }
  }, [country, states]);

  useEffect(() => {
    if (initialCountry && initialState && initialStartDate) {
      setCountry(initialCountry);
      setState(initialState);
      setLivedSinceYear(initialStartDate.year.toString());
      const displayMonth = (parseInt(initialStartDate.month) + 1).toString();
      setLivedSinceMonth(displayMonth);
      setStartDate(initialStartDate);
    }
  }, [initialCountry, initialState, initialStartDate]);

  useEffect(() => {
    if (livedSinceYear && livedSinceMonth) {
      setStartDate({
        year: livedSinceYear.toString(),
        month: (parseInt(livedSinceMonth) - 1).toString(),
        day: '01',
      });
    }
  }, [livedSinceMonth, livedSinceYear]);

  const addPreviousResidence = useCallback(() => {
    if (!livedSinceYear || !livedSinceMonth || !startDate) return;

    const updateDetail = {
      countryCode: country.value,
      stateCode: state.value,
      startMonth: parseInt(livedSinceMonth) - 1,
      startYear: parseInt(livedSinceYear),
    };

    if (index > -1) updateDetail.index = index;
    updateResidence(updateDetail);
    setEditState(false);
  }, [
    country,
    state,
    livedSinceMonth,
    livedSinceYear,
    startDate,
    index,
    updateResidence,
    setEditState,
  ]);

  useEffect(() => {
    if (startDate && !initialStartDate) {
      addPreviousResidence();
    }
  }, [startDate, initialStartDate, addPreviousResidence]);

  const handleChangeCountry = country => {
    setCountry(country);
    if (country?.value && !states[country.value]) {
      fetchStates(country.value);
    }
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const months = [
    { label: 'Month', value: '' },
    ...Array.from({ length: 12 }, (_, i) => ({
      label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
      value: (i + 1).toString(),
    })).filter(month => {
      if (livedSinceYear === currentYear.toString()) {
        return parseInt(month.value) - 1 <= currentMonth;
      }
      return true;
    }),
  ];

  const years = [
    { label: 'Year', value: '' },
    ...Array.from({ length: 60 }, (_, i) => {
      const year = (currentYear - i).toString();
      return { label: year, value: year };
    }),
  ];

  const handleChangeYear = year => {
    setLivedSinceYear(year);
    if (parseInt(year) === currentYear) {
      setLivedSinceMonth('');

      if (initialCountry && initialState && initialStartDate) {
        setShowMonthWarning(true);
      }
    }

    if (year === '') {
      setShowYearWarning(true);
    } else {
      setShowYearWarning(false);
    }
  };

  const handleChangeMonth = month => {
    setLivedSinceMonth(month);

    if (initialCountry && initialState && initialStartDate) {
      if (month !== '') {
        setShowMonthWarning(false);
      } else {
        setShowMonthWarning(true);
      }
    }
  };

  return (
    <div>
      <div className="resident_selection">
        <SelectBase
          label="Country"
          selectedValue={country}
          onChange={handleChangeCountry}
          options={countries}
          placeholder={constants.COUNTRY_PLACEHOLDER}
          noOptionsMessage="Loading countries..."
          autoFocus={false}
        />
      </div>
      <div className="resident_selection">
        {country && (
          <SelectBase
            label="State"
            selectedValue={state}
            options={stateOptions}
            onChange={setState}
            placeholder={constants.STATE_PLACEHOLDER}
            noOptionsMessage="Loading states..."
            autoFocus={false}
          />
        )}
      </div>
      <div className="resident_selection">
        {state && (
          <>
            <label htmlFor="lived-since-year" className="desktop-d-helper">
              I've lived here since
            </label>
            <div className="row row-padding-top">
              <div className="col col--7/16">
                <Select
                  id="lived-since-year"
                  data-testid="lived-since-year"
                  options={years}
                  value={livedSinceYear}
                  onChange={e => handleChangeYear(e.target.value)}
                  placeHolderClassName={showYearWarning ? 'select__placeholder__warning' : null}
                />
              </div>
              <div className="col col--9/16">
                <Select
                  id="lived-since-month"
                  data-testid="lived-since-month"
                  options={months}
                  value={livedSinceMonth}
                  onChange={e => handleChangeMonth(e.target.value)}
                  disabled={livedSinceYear === ''}
                  placeHolderClassName={showMonthWarning ? 'select__placeholder__warning' : null}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {initialCountry && initialState && initialStartDate && (
        <div className="row-spacer flex gap-4 mt-4">
          <Button
            type="button"
            className="niso-button cancel-button"
            onClick={() => setEditState(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="niso-button save-button"
            onClick={addPreviousResidence}
            disabled={!livedSinceMonth || !livedSinceYear}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

ResidenceSelection.propTypes = {
  updateResidence: PropTypes.func.isRequired,
  fetchStates: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  states: PropTypes.shape({}),
  setEditState: PropTypes.func.isRequired,
  initialCountry: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  initialState: PropTypes.shape({}),
  index: PropTypes.number,
  initialStartDate: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
};

ResidenceSelection.defaultProps = {
  countries: [],
  states: {},
  initialCountry: null,
  initialState: null,
  index: -1,
};

export default ResidenceSelection;
